/**
 * Agent-coercion review sub-step.
 *
 * Shows each invalid record as a card of "CSV value → corrected value" cells
 * (reusing the proposal PropertyDiffTable, which has inline per-field editing).
 * The user accepts / edits / clears each proposed value; a read-only backend
 * validation reports, per record, whether it would now import. "Apply fixes"
 * commits the accepted values and the backend re-analyzes so fixed rows
 * re-categorize (create/update).
 */

import { CheckCircle } from '@untitledui/icons';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PropertyDiffRow } from '@/components/proposals/artifacts/PropertyDiffTable';
import { PropertyDiffTable } from '@/components/proposals/artifacts/PropertyDiffTable';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import type {
  PropertySchemaMap,
  PropertyTypeSchema,
} from '@/lib/crm/hooks/usePropertySchema';
import { getEntityTypeLabel } from '@/lib/crm/utils/entityTypeUtils';
import {
  type AnalyzedRow,
  applyCoercions,
  type CoercionDecision,
  type CoercionOverrides,
  type CsvImport,
  getFileResults,
  type NewPropertySuggestion,
  validateCoercions,
} from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

import { useEntitySchemaMap } from '../../hooks/useEntitySchemaMap';
import { getPropertyLabel } from '../summary/types';
import { RecordPreviewCard } from './RecordPreviewCard';

const VALIDATION_DEBOUNCE_MS = 500;

interface InvalidCell {
  propertyName: string;
  csvValue: unknown;
  hasProposal: boolean;
}

export interface InvalidRecord {
  key: string; // `${fileId}:${rowIndex}`
  fileId: string;
  rowIndex: number;
  entityType: string;
  cells: InvalidCell[];
  /** The row's full mapped data, used to preview the record being created. */
  data: Record<string, unknown>;
}

function cellKey(
  fileId: string,
  rowIndex: number,
  propertyName: string
): string {
  return `${fileId}:${rowIndex}:${propertyName}`;
}

/**
 * Synthesize a PropertyTypeSchema for a property the import will CREATE but that
 * is not yet in the org schema, so the coercion widget picks the right editor
 * (e.g. the phone country picker for `phone_number`, option lists for selects).
 */
function newPropertyToSchema(np: NewPropertySuggestion): PropertyTypeSchema {
  return {
    property_type_name: np.property_name,
    entity_type_name: np.entity_type_name,
    label: np.label,
    field_type_name: np.field_type_name,
    description: '',
    is_required: false,
    is_unique_value: false,
    value_write_access: 'all',
    value_read_access: 'all',
    value_update_access: 'all',
    options: np.options,
  };
}

/**
 * Build the subset of `edits` that belongs to a single record (only the keys
 * prefixed by that record's `fileId:rowIndex:`).
 */
function buildScopedEdits(
  record: InvalidRecord,
  edits: Record<string, unknown>
): Record<string, unknown> {
  const scoped: Record<string, unknown> = {};
  for (const cell of record.cells) {
    const key = cellKey(record.fileId, record.rowIndex, cell.propertyName);
    scoped[key] = edits[key];
  }
  return scoped;
}

/**
 * Derive a per-record `edits` subset, keeping the same object reference for a
 * record across renders unless that record's own cell values changed. This
 * keeps sibling `CoercionRecordCard`s from recomputing their `rows` memo when
 * a keystroke lands in an unrelated record.
 */
function useRecordEditsByKey(
  records: InvalidRecord[],
  edits: Record<string, unknown>
): Record<string, Record<string, unknown>> {
  const cacheRef = useRef<Map<string, Record<string, unknown>>>(new Map());

  return useMemo(() => {
    const previous = cacheRef.current;
    const next = new Map<string, Record<string, unknown>>();
    const result: Record<string, Record<string, unknown>> = {};
    for (const record of records) {
      const scoped = buildScopedEdits(record, edits);
      const cached = previous.get(record.key);
      const scopedKeys = Object.keys(scoped);
      const unchanged =
        cached !== undefined &&
        scopedKeys.length === Object.keys(cached).length &&
        scopedKeys.every(k => cached[k] === scoped[k]);
      const value = unchanged && cached !== undefined ? cached : scoped;
      next.set(record.key, value);
      result[record.key] = value;
    }
    cacheRef.current = next;
    return result;
  }, [records, edits]);
}

export interface InvalidRowItem {
  fileId: string;
  row: AnalyzedRow;
}

/**
 * Build invalid records and their seeded edits from explicit
 * `(fileId, row)` items, independent of where the rows came from (inline
 * `analysis_result` or the paginated `/rows` endpoint). Each cell's initial
 * edit value is seeded from the agent proposal (or the CSV value when there
 * is no proposal, so the input is still editable).
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with component
export function buildInvalidRecords(
  items: InvalidRowItem[],
  uploadedFiles: CsvImport['uploaded_files']
): {
  records: InvalidRecord[];
  initialEdits: Record<string, unknown>;
} {
  const entityTypeByFile: Record<string, string> = {};
  for (const file of uploadedFiles) {
    entityTypeByFile[String(file.file_id)] = file.entity_type_name;
  }

  const records: InvalidRecord[] = [];
  const initialEdits: Record<string, unknown> = {};

  for (const { fileId, row } of items) {
    if (row.action !== 'invalid' || !row.errors?.length) {
      continue;
    }
    const entityType = entityTypeByFile[fileId] ?? 'ahoy_contact';
    const seen = new Set<string>();
    const cells: InvalidCell[] = [];
    for (const err of row.errors) {
      const propertyName = err.property_name;
      if (!propertyName || seen.has(propertyName)) {
        continue;
      }
      seen.add(propertyName);
      const csvValue = (row.data ?? {})[propertyName];
      const proposal = row.proposals?.[propertyName];
      const savedEdit = row.coercion_edits?.[propertyName];
      cells.push({
        propertyName,
        csvValue,
        hasProposal: proposal !== undefined,
      });
      // Seed precedence: the user's saved edit wins, then the agent proposal,
      // then the original (still-invalid) CSV value.
      let seed: unknown = csvValue;
      if (proposal !== undefined) {
        seed = proposal.proposed_value;
      }
      if (savedEdit !== undefined) {
        seed = savedEdit;
      }
      initialEdits[cellKey(fileId, row.row_index, propertyName)] = seed;
    }
    if (cells.length > 0) {
      records.push({
        key: `${fileId}:${row.row_index}`,
        fileId,
        rowIndex: row.row_index,
        entityType,
        cells,
        data: row.data ?? {},
      });
    }
  }

  return { records, initialEdits };
}

/**
 * Collect the invalid records and their failing cells from the inline
 * analysis result (small-import path).
 */
function collectInvalidRecords(importData: CsvImport): {
  records: InvalidRecord[];
  initialEdits: Record<string, unknown>;
} {
  const items: InvalidRowItem[] = [];
  const fileResults = getFileResults(importData.analysis_result ?? null);
  for (const [fileId, fileData] of Object.entries(fileResults)) {
    for (const row of fileData.analyzed_rows ?? []) {
      items.push({ fileId, row });
    }
  }
  return buildInvalidRecords(items, importData.uploaded_files);
}

interface CoercionRecordCardProps {
  record: InvalidRecord;
  schemaMap: PropertySchemaMap;
  newPropertySchemaByName: Record<string, PropertyTypeSchema>;
  edits: Record<string, unknown>;
  rowValid: boolean | undefined;
  orgId: number;
  onCellChange: (
    fileId: string,
    rowIndex: number,
    propertyName: string,
    newValue: unknown
  ) => void;
}

function CoercionRecordCard({
  record,
  schemaMap,
  newPropertySchemaByName,
  edits,
  rowValid,
  orgId,
  onCellChange,
}: CoercionRecordCardProps) {
  const rows = useMemo<PropertyDiffRow[]>(
    () =>
      record.cells.map(cell => {
        const schema = schemaMap[cell.propertyName];
        // Existing org schema wins; otherwise fall back to the to-be-created
        // property's planned field type/options so the correct editor renders.
        const effectiveSchema =
          schema ?? newPropertySchemaByName[cell.propertyName];
        const key = cellKey(record.fileId, record.rowIndex, cell.propertyName);
        return {
          propertyName: cell.propertyName,
          propertyLabel: getPropertyLabel(cell.propertyName, schemaMap, {}),
          fieldType: effectiveSchema?.field_type_name ?? 'text',
          currentValue: cell.csvValue,
          proposedValue: edits[key],
          originalValue: cell.csvValue,
          hasConflict: false,
          propertySchema: effectiveSchema,
        };
      }),
    [record, schemaMap, edits, newPropertySchemaByName]
  );

  const handleValueChange = useCallback(
    (propertyName: string, newValue: unknown) =>
      onCellChange(record.fileId, record.rowIndex, propertyName, newValue),
    [onCellChange, record.fileId, record.rowIndex]
  );

  return (
    <div className='rounded-xl border border-border-secondary bg-bg-primary p-4'>
      <div className='flex items-center justify-between mb-3'>
        <span className='text-sm font-semibold text-fg-primary'>
          {getEntityTypeLabel(record.entityType)} · Row {record.rowIndex + 1}
        </span>
        {rowValid ? (
          <Badge size='sm' color='success' className='gap-1'>
            <CheckCircle className='w-3 h-3' />
            Ready to import
          </Badge>
        ) : (
          <Badge size='sm' color='warning'>
            Still needs a fix
          </Badge>
        )}
      </div>
      <div className='mb-3'>
        <RecordPreviewCard
          entityType={record.entityType}
          data={record.data}
          orgId={orgId}
        />
      </div>
      <PropertyDiffTable
        rows={rows}
        editable={true}
        onValueChange={handleValueChange}
        entityType={record.entityType}
      />
    </div>
  );
}

interface CoercionReviewProps {
  importData: CsvImport;
  orgId: number;
  importId: number;
  onBack: () => void;
  onApplied: (updated: CsvImport) => void;
  /** Hide the internal title/description when a container (e.g. the modal
   *  dialog header) already titles this view. Back stays visible. */
  showHeader?: boolean;
  /** When true, the Back/Apply controls live in the parent's chrome (e.g. the
   *  modal Dialog footer) rather than inline. The parent triggers Apply via
   *  {@link applyControlsRef} and mirrors button state via
   *  {@link onFooterStateChange}. Defaults to false (inline controls), which
   *  the wizard sub-step relies on. */
  externalFooter?: boolean;
  /** Populated by this component with its current apply handler so the parent's
   *  footer Apply button can invoke it. Only used when externalFooter is true. */
  applyControlsRef?: RefObject<(() => void) | null>;
  /** Reports the derived footer state (primitives only, so it can't cause a
   *  render loop) so the parent can render an equivalent Apply button. */
  onFooterStateChange?: (state: {
    readyCount: number;
    isApplying: boolean;
  }) => void;
  /** Large-import path: build records from these (file_id-tagged) rows
   *  instead of omitted inline rows. */
  injectedRows?: InvalidRowItem[];
}

export function CoercionReview({
  importData,
  orgId,
  importId,
  onBack,
  onApplied,
  showHeader = true,
  externalFooter = false,
  applyControlsRef,
  onFooterStateChange,
  injectedRows,
}: CoercionReviewProps) {
  const { records, initialEdits } = useMemo(
    () =>
      injectedRows
        ? buildInvalidRecords(injectedRows, importData.uploaded_files)
        : collectInvalidRecords(importData),
    [injectedRows, importData]
  );

  const [edits, setEdits] = useState<Record<string, unknown>>(initialEdits);
  const [validityByRecord, setValidityByRecord] = useState<
    Record<string, boolean>
  >({});
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [hideReady, setHideReady] = useState(false);
  // The record whose cell was most recently edited. Kept visible even when
  // `hideReady` is on and the record just validated, so the "hide
  // ready-to-import" filter never yanks away a record the user is actively
  // editing. It stops being "active" once a different record is edited.
  const [activeRecordKey, setActiveRecordKey] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reconcile edit + validity state when the injected record set changes.
  // `edits`/`validityByRecord`/`activeRecordKey` are seeded once at mount from
  // the initial records; when `injectedRows` later swaps in a different set of
  // rows (a `/rows` refetch or a page change in the large-import path), the
  // derived `records`/`initialEdits` recompute but that state does not — leaving
  // stale proposals attached to newly-loaded records and validity reused across
  // a different row. Adjust the state during render (React's "adjusting state
  // when a prop changes" pattern) rather than in an effect, so no extra commit
  // happens: preserve the user's deliberate edits, reseed everything else from
  // the fresh proposals, and drop keys for rows gone.
  //
  // The signature includes the incoming seed *values*, not just the cell keys,
  // so a refetch that updates a proposal or saved edit for the same cell (same
  // key, new value) still triggers reconciliation — a key-only signature would
  // miss it and keep displaying/submitting the stale value. `seededEditsRef`
  // remembers the last seeds applied so we can tell an untouched seed (reseed
  // it from the fresh value) from a value the user edited away from its seed
  // (preserve it).
  const initialEditsSignature = useMemo(
    () =>
      Object.keys(initialEdits)
        .sort()
        .map(key => `${key}=${JSON.stringify(initialEdits[key])}`)
        .join('|'),
    [initialEdits]
  );
  const reconciledSignatureRef = useRef(initialEditsSignature);
  const seededEditsRef = useRef(initialEdits);
  if (reconciledSignatureRef.current !== initialEditsSignature) {
    reconciledSignatureRef.current = initialEditsSignature;
    const prevSeeds = seededEditsRef.current;
    seededEditsRef.current = initialEdits;
    // Reconcile the edit map: preserve a value the user deliberately edited
    // away from its last seed; otherwise take the fresh incoming seed so an
    // updated proposal or saved edit replaces stale state instead of sticking
    // to the old one. Computed against the render-scoped `edits` (the current
    // committed state) so we can also detect which records' values changed.
    const nextEdits: Record<string, unknown> = {};
    for (const key of Object.keys(initialEdits)) {
      const userEdited = key in edits && edits[key] !== prevSeeds[key];
      nextEdits[key] = userEdited ? edits[key] : initialEdits[key];
    }
    setEdits(nextEdits);
    // Records whose effective value set changed in this reconciliation. Their
    // stored validity was computed against the OLD values, so it is now stale:
    // a refreshed proposal (same record key, new value) would otherwise stay
    // marked ready and be applied before the new value re-validates. Clearing
    // it keeps Apply disabled until validation reflects the fresh choices.
    const changedRecordKeys = new Set<string>();
    for (const record of records) {
      for (const cell of record.cells) {
        const key = cellKey(record.fileId, record.rowIndex, cell.propertyName);
        // Compare key *presence* as well as value: a newly seeded cell whose
        // fresh value is `undefined` reads the same as an absent key under a
        // value-only compare (`undefined !== undefined` is false), so the
        // record would keep its stale `row_valid` and stay applyable without
        // re-validating the new cell. Treating an absent committed key as a
        // change forces re-validation of the fresh decision set.
        if (!(key in edits) || nextEdits[key] !== edits[key]) {
          changedRecordKeys.add(record.key);
          break;
        }
      }
    }
    setValidityByRecord(prev => {
      const liveKeys = new Set(records.map(r => r.key));
      const next: Record<string, boolean> = {};
      for (const key of Object.keys(prev)) {
        // Keep validity only for records that still exist AND whose values did
        // not change in this reconciliation.
        if (liveKeys.has(key) && !changedRecordKeys.has(key)) {
          next[key] = prev[key];
        }
      }
      return next;
    });
    setActiveRecordKey(prev =>
      prev !== null && records.some(r => r.key === prev) ? prev : null
    );
  }

  const { schemaMap } = useEntitySchemaMap(orgId, importData.uploaded_files);

  const newPropertySchemaByName = useMemo(() => {
    const map: Record<string, PropertyTypeSchema> = {};
    for (const file of importData.uploaded_files) {
      for (const np of file.new_properties ?? []) {
        if (!(np.property_name in map)) {
          map[np.property_name] = newPropertyToSchema(np);
        }
      }
    }
    return map;
  }, [importData.uploaded_files]);

  const onCellChange = useCallback(
    (
      fileId: string,
      rowIndex: number,
      propertyName: string,
      newValue: unknown
    ) => {
      setActiveRecordKey(`${fileId}:${rowIndex}`);
      setEdits(prev => ({
        ...prev,
        [cellKey(fileId, rowIndex, propertyName)]: newValue,
      }));
    },
    []
  );

  // Build the per-record decision payload from the current edits.
  const decisions = useMemo<CoercionDecision[]>(
    () =>
      records.map(record => {
        const values: Record<string, unknown> = {};
        for (const cell of record.cells) {
          values[cell.propertyName] =
            edits[cellKey(record.fileId, record.rowIndex, cell.propertyName)];
        }
        return { file_id: record.fileId, row_index: record.rowIndex, values };
      }),
    [records, edits]
  );

  // Debounced read-only validation of the current choices.
  useEffect(() => {
    if (records.length === 0) {
      return;
    }
    // Guard against out-of-order responses: when a newer validation cycle starts (deps
    // change) or the component unmounts, the cleanup flips `cancelled` so an already
    // in-flight request's result is ignored and can't overwrite fresher validity state.
    let cancelled = false;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      validateCoercions(orgId, importId, decisions)
        .then(results => {
          if (cancelled) {
            return;
          }
          const next: Record<string, boolean> = {};
          for (const result of results) {
            next[`${result.file_id}:${result.row_index}`] = result.row_valid;
          }
          setValidityByRecord(next);
        })
        .catch(err => {
          if (cancelled) {
            return;
          }
          logger.error(
            'Failed to validate coercions',
            err instanceof Error ? err : new Error(String(err)),
            { import_id: importId, org_id: orgId }
          );
        });
    }, VALIDATION_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [decisions, orgId, importId, records.length]);

  const readyCount = useMemo(
    () => records.filter(r => validityByRecord[r.key]).length,
    [records, validityByRecord]
  );

  const visibleRecords = useMemo(
    () =>
      hideReady
        ? records.filter(
            r => r.key === activeRecordKey || !validityByRecord[r.key]
          )
        : records,
    [hideReady, records, validityByRecord, activeRecordKey]
  );

  const recordEditsByKey = useRecordEditsByKey(records, edits);

  const handleApply = useCallback(async () => {
    // Only commit records that would actually import (valid), sending their edited cells.
    const overrides: CoercionOverrides = {};
    for (const record of records) {
      if (!validityByRecord[record.key]) {
        continue;
      }
      for (const cell of record.cells) {
        const key = cellKey(record.fileId, record.rowIndex, cell.propertyName);
        overrides[key] = edits[key];
      }
    }
    if (Object.keys(overrides).length === 0) {
      return;
    }
    setIsApplying(true);
    setApplyError(null);
    try {
      const updated = await applyCoercions(orgId, importId, overrides);
      onApplied(updated);
    } catch (err) {
      logger.error(
        'Failed to apply coercions',
        err instanceof Error ? err : new Error(String(err)),
        { import_id: importId, org_id: orgId }
      );
      setApplyError(
        'Unable to apply the fixes. Please try again or contact support if the issue persists.'
      );
    } finally {
      setIsApplying(false);
    }
  }, [records, validityByRecord, edits, orgId, importId, onApplied]);

  // Expose the current apply handler to the parent's footer (external-footer mode).
  // A ref sync avoids threading a changing function identity through props/deps.
  useEffect(() => {
    if (!externalFooter || !applyControlsRef) {
      return;
    }
    applyControlsRef.current = handleApply;
    return () => {
      applyControlsRef.current = null;
    };
  }, [externalFooter, applyControlsRef, handleApply]);

  // Mirror the derived footer state to the parent. Only primitives flow up, so
  // the parent re-render can't feed back into a loop here.
  useEffect(() => {
    if (!externalFooter) {
      return;
    }
    onFooterStateChange?.({ readyCount, isApplying });
  }, [externalFooter, onFooterStateChange, readyCount, isApplying]);

  return (
    <div className='space-y-4'>
      {(showHeader || !externalFooter) && (
        <div className='flex items-start justify-between gap-4'>
          {showHeader ? (
            <div>
              <h3 className='text-base font-semibold text-fg-primary'>
                Review suggested fixes
              </h3>
              <p className='text-sm text-fg-tertiary mt-0.5'>
                Our agent proposed corrected values for rows that couldn&apos;t
                be imported. Accept, edit, or clear each one – then apply the
                fixes you want.
              </p>
            </div>
          ) : (
            <div />
          )}
          {!externalFooter && (
            <Button color='secondary' size='sm' onClick={onBack}>
              Back
            </Button>
          )}
        </div>
      )}

      {applyError && (
        <div
          role='alert'
          className='p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'
        >
          <p className='text-sm text-utility-error-700'>{applyError}</p>
        </div>
      )}

      {readyCount > 0 && (
        <div className='flex items-center justify-between gap-4 rounded-lg border border-border-secondary bg-bg-secondary px-3 py-2'>
          <span className='text-sm text-fg-secondary'>
            {readyCount} of {records.length} ready to import
          </span>
          <Checkbox
            size='sm'
            isSelected={hideReady}
            onChange={setHideReady}
            label='Hide ready-to-import rows'
          />
        </div>
      )}

      {records.length === 0 && (
        <div className='text-sm text-fg-tertiary italic py-4'>
          There are no invalid rows to fix.
        </div>
      )}

      {records.length > 0 && visibleRecords.length === 0 && (
        <div className='flex items-center gap-2 py-4 text-sm text-utility-success-700'>
          <CheckCircle className='h-4 w-4' />
          All rows are ready to import.
        </div>
      )}

      {visibleRecords.length > 0 && (
        <div className='space-y-4'>
          {visibleRecords.map(record => (
            <CoercionRecordCard
              key={record.key}
              record={record}
              schemaMap={schemaMap}
              newPropertySchemaByName={newPropertySchemaByName}
              edits={recordEditsByKey[record.key] ?? {}}
              rowValid={validityByRecord[record.key]}
              orgId={orgId}
              onCellChange={onCellChange}
            />
          ))}
        </div>
      )}

      {!externalFooter && (
        <div className='flex items-center justify-end gap-3 pt-2'>
          <Button
            color='primary'
            size='md'
            onClick={handleApply}
            isDisabled={readyCount === 0 || isApplying}
            isLoading={isApplying}
          >
            {readyCount > 0
              ? `Apply ${readyCount} fix${readyCount === 1 ? '' : 'es'}`
              : 'Apply fixes'}
          </Button>
        </div>
      )}
    </div>
  );
}
